/**
 * The favourite toggle: a star that pins the page or project to the start page. State lives
 * on the server per user; the component asks once on mount and toggles optimistically.
 */
import { useEffect, useState } from "react";

import { api, type FavoriteKind } from "../api";
import { useToast } from "./toast";

export function FavoriteStar({ kind, targetId }: { kind: FavoriteKind; targetId: string }) {
  const showToast = useToast();
  const [isFavorite, setIsFavorite] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.favorites().then((favorites) => {
      if (!cancelled) {
        setIsFavorite(favorites.some((f) => f.kind === kind && f.target_id === targetId));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [kind, targetId]);

  if (isFavorite === null) {
    return null;
  }

  const toggle = async () => {
    setIsFavorite(!isFavorite);
    try {
      if (isFavorite) {
        await api.removeFavorite(kind, targetId);
      } else {
        await api.addFavorite(kind, targetId);
      }
    } catch {
      setIsFavorite(isFavorite);
      showToast({ message: "Kunde inte uppdatera favoriten.", tone: "fail" });
    }
  };

  return (
    <button
      type="button"
      aria-pressed={isFavorite}
      aria-label={isFavorite ? "Ta bort från favoriter" : "Lägg till som favorit"}
      title={isFavorite ? "Favorit — klicka för att ta bort" : "Gör till favorit"}
      onClick={() => void toggle()}
      className={`rounded-control px-s3 py-s2 text-title leading-none transition-[color] duration-control ease-nb ${
        isFavorite ? "text-drop" : "text-muted hover:text-ink"
      }`}
    >
      {isFavorite ? "★" : "☆"}
    </button>
  );
}
